import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { registerToastHandlers } from "./toast";

const ToastContext = createContext(null);

let nextId = 1;

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  // { message, confirmText, cancelText, resolve } | null
  const [confirmState, setConfirmState] = useState(null);

  const dismiss = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const push = useCallback(
    (message, { type = "success", duration = 4000 } = {}) => {
      const id = nextId++;
      setToasts((prev) => [...prev, { id, message, type }]);
      if (duration > 0) {
        setTimeout(() => dismiss(id), duration);
      }
      return id;
    },
    [dismiss]
  );

  // Promise-based confirm so callers can `await confirmDialog(...)`.
  const confirm = useCallback(
    (message, { confirmText = "Confirm", cancelText = "Cancel" } = {}) =>
      new Promise((resolve) => {
        setConfirmState({ message, confirmText, cancelText, resolve });
      }),
    []
  );

  const resolveConfirm = useCallback((result) => {
    setConfirmState((prev) => {
      prev?.resolve(result);
      return null;
    });
  }, []);

  // Expose push/confirm to the imperative bridge so non-hook code can use them.
  useEffect(() => {
    registerToastHandlers({ push, confirm });
  }, [push, confirm]);

  const toast = {
    success: (msg, opts) => push(msg, { ...opts, type: "success" }),
    error: (msg, opts) => push(msg, { ...opts, type: "error" }),
    info: (msg, opts) => push(msg, { ...opts, type: "info" }),
  };

  return (
    <ToastContext.Provider value={toast}>
      {children}
      <ToastViewport toasts={toasts} onDismiss={dismiss} />
      {confirmState && (
        <ConfirmDialog
          state={confirmState}
          onCancel={() => resolveConfirm(false)}
          onConfirm={() => resolveConfirm(true)}
        />
      )}
    </ToastContext.Provider>
  );
};

const ConfirmDialog = ({ state, onCancel, onConfirm }) => (
  <div
    className="fixed inset-0 z-[110] flex items-center justify-center bg-black/40 px-4"
    role="dialog"
    aria-modal="true"
  >
    <div className="w-full max-w-md rounded-lg bg-white shadow-xl">
      <div className="flex items-start gap-3 p-5">
        <span className="shrink-0 mt-0.5 text-amber-500">
          <svg className="w-6 h-6" viewBox="0 0 20 20" fill="currentColor">
            <path
              fillRule="evenodd"
              d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
              clipRule="evenodd"
            />
          </svg>
        </span>
        <div className="flex-1">
          <h3 className="text-base font-semibold text-gray-900">Confirm</h3>
          <p className="mt-1 text-sm text-gray-600">{state.message}</p>
        </div>
      </div>
      <div className="flex justify-end gap-2 border-t border-gray-100 px-5 py-3">
        <button
          onClick={onCancel}
          className="px-4 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50"
        >
          {state.cancelText}
        </button>
        <button
          onClick={onConfirm}
          className="px-4 py-1.5 text-sm font-medium text-white bg-red-600 rounded hover:bg-red-700"
        >
          {state.confirmText}
        </button>
      </div>
    </div>
  </div>
);

export const useToast = () => {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used within a ToastProvider");
  return ctx;
};

const ToastViewport = ({ toasts, onDismiss }) => (
  <div className="fixed top-5 right-5 z-[100] flex flex-col gap-2 pointer-events-none">
    {toasts.map((t) => (
      <ToastItem key={t.id} toast={t} onDismiss={() => onDismiss(t.id)} />
    ))}
  </div>
);

const ICONS = {
  success: (
    <svg className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor">
      <path
        fillRule="evenodd"
        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
        clipRule="evenodd"
      />
    </svg>
  ),
  error: (
    <svg className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor">
      <path
        fillRule="evenodd"
        d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
        clipRule="evenodd"
      />
    </svg>
  ),
  info: (
    <svg className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor">
      <path
        fillRule="evenodd"
        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
        clipRule="evenodd"
      />
    </svg>
  ),
};

const STYLES = {
  success: "bg-white border-l-4 border-emerald-500 text-gray-800",
  error: "bg-white border-l-4 border-red-500 text-gray-800",
  info: "bg-white border-l-4 border-blue-500 text-gray-800",
};

const ICON_COLORS = {
  success: "text-emerald-500",
  error: "text-red-500",
  info: "text-blue-500",
};

const ToastItem = ({ toast, onDismiss }) => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const id = requestAnimationFrame(() => setVisible(true));
    return () => cancelAnimationFrame(id);
  }, []);

  return (
    <div
      className={`pointer-events-auto min-w-[280px] max-w-sm rounded-lg shadow-lg flex items-start gap-3 px-4 py-3 transition-all duration-200 ${
        STYLES[toast.type] || STYLES.info
      } ${visible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-4"}`}
      role="status"
    >
      <span className={`shrink-0 mt-0.5 ${ICON_COLORS[toast.type] || ICON_COLORS.info}`}>
        {ICONS[toast.type] || ICONS.info}
      </span>
      <p className="text-sm leading-snug flex-1">{toast.message}</p>
      <button
        onClick={onDismiss}
        className="shrink-0 text-gray-400 hover:text-gray-600 transition-colors"
        aria-label="Dismiss"
      >
        <svg className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
          <path
            fillRule="evenodd"
            d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
            clipRule="evenodd"
          />
        </svg>
      </button>
    </div>
  );
};
